
import React from 'react';
import { Link } from 'react-router-dom';
import { Terminal, CheckCircle, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const PromptEngineering = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Prompt Engineering for Sales Training | BPO Consultants"
                description="Master ChatGPT and Claude for sales. Learn to write high-converting prompts, automate scripts, and handle objections with AI."
                keywords="Prompt Engineering for Sales, AI Sales Training, ChatGPT for Business Development, Generative AI Course, Sales Script Automation"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=1200" alt="Code on screen" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Terminal size={14} className="inline mr-1" /> Module 1: Foundation
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Prompt Engineering <br /> for Sales Professionals
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            Stop using generic AI responses. Learn the art of crafting precise prompts that generate revenue-driving copy.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Curriculum Content */}
            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">What You Will Learn</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            This course goes beyond basic "write me an email" commands. We dive deep into context injection, few-shot prompting, and chain-of-thought reasoning to turn LLMs into elite sales assistants.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "Context Injection",
                                    desc: "How to feed the AI your company's value proposition case studies, and tone guidelines so it sounds exactly like you."
                                },
                                {
                                    title: "Objection Handling Bots",
                                    desc: "Build custom GPTs that roleplay as difficult prospects to practice your negotiation skills in real-time."
                                },
                                {
                                    title: "Sequence Generation",
                                    desc: "Create entire 7-step email sequences in minutes, tailored to specific industries and buyer personas."
                                },
                                {
                                    title: "Research Automation",
                                    desc: "Use AI to summarize annual reports and news articles to find 'trigger events' for outreach."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <CheckCircle size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    {/* SGE / FAQ Section */}
                    <FadeIn className="mb-16">
                        <h2 className="text-2xl font-bold text-[#20222D] mb-6">Common Questions</h2>
                        <div className="space-y-4">
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">Do I need coding experience?</h3>
                                <p className="text-gray-600">No. Prompt engineering is about using natural language effectively. If you can speak English, you can learn to prompt.</p>
                            </div>
                            <div className="border-b border-gray-200 pb-4">
                                <h3 className="font-bold text-gray-900 mb-2">Which AI models do you cover?</h3>
                                <p className="text-gray-600">We verify skills on OpenAI's GPT-4, Anthropic's Claude 3, and Perplexity for research.</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 mb-2">How does this help my career?</h3>
                                <p className="text-gray-600">AI-enabled sales reps are reportedly 40% more productive. Mastering these tools makes you future-proof and highly employable.</p>
                            </div>
                        </div>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Start Learning</h2>
                        <p className="text-gray-300 mb-8 max-w-xl mx-auto">
                            Join the waitlist for our next cohort or access the self-paced materials immediately.
                        </p>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Enroll Now <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default PromptEngineering;
